export const config = window.APP_CONFIG || {};

export const todayISO = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

export const startOfWeekISO = (date = new Date()) => {
  const d = new Date(date);
  const day = d.getDay() || 7;
  d.setDate(d.getDate() - day + 1);
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

export const escapeHtml = (value = '') => String(value)
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#039;');

export const formatDate = (value, options = {}) => {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(`${value}T12:00:00`);
  return new Intl.DateTimeFormat('fr-CA', {
    weekday: options.weekday ?? 'long',
    year: options.year ?? 'numeric',
    month: options.month ?? 'long',
    day: options.day ?? 'numeric'
  }).format(date);
};

export const formatShortDate = (value) => {
  if (!value) return '—';
  return new Intl.DateTimeFormat('fr-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(new Date(`${value}T12:00:00`));
};

export const formatTime = (value) => {
  if (!value) return '—';
  return new Intl.DateTimeFormat('fr-CA', {
    hour: '2-digit',
    minute: '2-digit',
    second: undefined
  }).format(new Date(value));
};

export const formatTimeInput = (value) => {
  if (!value) return '';
  if (/^\d{2}:\d{2}/.test(value)) return value.slice(0, 5);
  const date = new Date(value);
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

export const minutesBetween = (start, end) => {
  if (!start || !end) return null;
  const minutes = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 60000);
  return Number.isFinite(minutes) ? Math.max(0, minutes) : null;
};

export const formatDuration = (minutes) => {
  if (minutes === null || minutes === undefined || Number.isNaN(minutes)) return '—';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder ? `${hours} h ${remainder} min` : `${hours} h`;
};

export const getTravelMinutes = (route, stop, previousStop) => {
  const start = previousStop?.departed_at || route?.started_at;
  return minutesBetween(start, stop?.arrived_at);
};

export const getServiceMinutes = (stop) => minutesBetween(stop?.arrived_at, stop?.departed_at);

export const getDelayMinutes = (routeDate, stop) => {
  if (!stop?.planned_arrival || !stop?.arrived_at) return null;
  const planned = new Date(`${routeDate}T${stop.planned_window_end || stop.planned_arrival}`);
  const actual = new Date(stop.arrived_at);
  return Math.max(0, Math.round((actual.getTime() - planned.getTime()) / 60000));
};

export const routeStatusLabel = (status) => ({
  planned: 'Planifiée',
  in_progress: 'En cours',
  completed: 'Terminée',
  cancelled: 'Annulée'
}[status] || status || '—');

export const stopStatusLabel = (status) => ({
  planned: 'À faire',
  arrived: 'Sur place',
  completed: 'Terminé',
  skipped: 'Ignoré'
}[status] || status || '—');

export const outcomeLabel = (outcome) => ({
  pickup: 'Ramassage effectué',
  delivery: 'Livraison effectuée',
  pickup_delivery: 'Ramassage et livraison',
  absent: 'Client absent',
  exception: 'Problème ou exception',
  other: 'Autre'
}[outcome] || outcome || '—');

export const uid = () => crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const geolocate = (options = {}) => new Promise((resolve, reject) => {
  if (!navigator.geolocation) {
    reject(new Error('La géolocalisation n’est pas disponible sur ce téléphone.'));
    return;
  }

  navigator.geolocation.getCurrentPosition(
    (position) => resolve({
      lat: position.coords.latitude,
      lng: position.coords.longitude,
      accuracy: position.coords.accuracy,
      capturedAt: new Date(position.timestamp).toISOString()
    }),
    (error) => {
      const message = {
        1: 'L’accès à la position a été refusé. Autorisez la localisation dans les réglages du navigateur.',
        2: 'La position n’a pas pu être déterminée.',
        3: 'La recherche de position a pris trop de temps.'
      }[error.code] || 'La position n’a pas pu être obtenue.';
      reject(new Error(message));
    },
    {
      enableHighAccuracy: true,
      timeout: options.timeout || 15000,
      maximumAge: options.maximumAge || 15000
    }
  );
});

export const mapLink = (address) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address || '')}`;

export const toCSV = (rows, columns) => {
  const quote = (value) => {
    if (value === null || value === undefined) return '';
    const text = String(value);
    return /[;"\n\r]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
  };

  const header = columns.map((column) => quote(column.label)).join(';');
  const body = rows.map((row) => columns.map((column) => quote(column.value(row))).join(';')).join('\r\n');
  return `\uFEFF${header}\r\n${body}`;
};

export const downloadText = (filename, content, type = 'text/csv;charset=utf-8') => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
